import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/layout/Layout';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import { usersApi } from '../api/users.api';
import { companyApi } from '../api/company.api';
import { Company } from '../types/user';
import { toast } from '../utils/toast';

// ─── Types ────────────────────────────────────────────────────────────────────

type Tab = 'profile' | 'company' | 'notifications';

interface ProfileForm {
  name: string;
  phone: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ProfileErrors {
  name?: string;
  phone?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

interface CompanyForm {
  name: string;
  contactEmail: string;
  address: string;
  phone: string;
}

interface NotificationPrefs {
  newTrips: boolean;
  qualificationReminders: boolean;
  emailNotifications: boolean;
}

const NOTIF_STORAGE_KEY = 'fleet_notification_prefs';

const defaultNotifPrefs: NotificationPrefs = {
  newTrips: true,
  qualificationReminders: true,
  emailNotifications: false,
};

// ─── Toggle component ─────────────────────────────────────────────────────────

interface ToggleProps {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
}

const Toggle: React.FC<ToggleProps> = ({ checked, onChange, label, description }) => (
  <div className="flex items-start justify-between gap-4 py-4">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      {description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
        checked ? 'bg-primary-600' : 'bg-gray-200'
      }`}
      role="switch"
      aria-checked={checked}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

// ─── Skeleton loader ──────────────────────────────────────────────────────────

const FieldSkeleton: React.FC = () => (
  <div className="space-y-4 animate-pulse">
    {[1, 2, 3, 4].map((i) => (
      <div key={i}>
        <div className="h-4 w-32 bg-gray-200 rounded mb-2" />
        <div className="h-9 w-full bg-gray-100 rounded-md" />
      </div>
    ))}
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

export const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();

  const isSuperAdmin = user?.isSuperAdmin === true;
  const isCompanyAdmin = user?.role === 'company_admin';

  // Entreprise tab: only for company_admin (super_admin manages companies from /admin/companies)
  const availableTabs: Tab[] = ['profile', ...(isCompanyAdmin ? ['company' as Tab] : []), 'notifications'];
  const [activeTab, setActiveTab] = useState<Tab>('profile');

  // ── Profile tab state ──────────────────────────────────────────────────────

  const [profileForm, setProfileForm] = useState<ProfileForm>({
    name: user?.name || '',
    phone: user?.phone || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [profileErrors, setProfileErrors] = useState<ProfileErrors>({});
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Sync form if user object changes (e.g. initial load)
  useEffect(() => {
    if (user) {
      setProfileForm((prev) => ({
        ...prev,
        name: user.name || '',
        phone: user.phone || '',
      }));
    }
  }, [user?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateProfile = (): boolean => {
    const errors: ProfileErrors = {};

    if (!profileForm.name.trim()) {
      errors.name = t('settings.validation.nameRequired');
    }

    if (profileForm.phone && !/^[+\d\s\-()]{6,20}$/.test(profileForm.phone.trim())) {
      errors.phone = t('settings.validation.phoneInvalid');
    }

    if (profileForm.newPassword) {
      if (profileForm.newPassword.length < 8) {
        errors.newPassword = t('settings.validation.passwordMinLength');
      } else if (!/[A-Z]/.test(profileForm.newPassword)) {
        errors.newPassword = t('settings.validation.passwordUppercase');
      } else if (!/\d/.test(profileForm.newPassword)) {
        errors.newPassword = t('settings.validation.passwordNumber');
      }

      if (profileForm.newPassword !== profileForm.confirmPassword) {
        errors.confirmPassword = t('settings.validation.passwordMismatch');
      }
    }

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSave = async () => {
    if (!user || !validateProfile()) return;

    setIsSavingProfile(true);
    try {
      const payload: { name?: string; phone?: string; password?: string } = {
        name: profileForm.name.trim(),
        phone: profileForm.phone.trim() || undefined,
      };

      if (profileForm.newPassword) {
        payload.password = profileForm.newPassword;
      }

      const updatedUser = await usersApi.updateUser(user._id, payload);

      // Update auth context so Navbar and other components reflect the change
      setUser({ ...user, ...updatedUser });
      // Persist updated user to localStorage (mirrors AuthContext login behaviour)
      localStorage.setItem('user', JSON.stringify({ ...user, ...updatedUser }));

      // Clear password fields after successful save
      setProfileForm((prev) => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));

      toast.success(t('settings.profile.savedSuccess'));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('settings.profile.saveFailed'));
    } finally {
      setIsSavingProfile(false);
    }
  };

  // ── Company tab state ──────────────────────────────────────────────────────

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [companyForm, setCompanyForm] = useState<CompanyForm>({
    name: '',
    contactEmail: '',
    address: '',
    phone: '',
  });
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const [isSavingCompany, setIsSavingCompany] = useState(false);

  const loadCompanyData = useCallback(async (companyId: string) => {
    if (!companyId) return;
    setIsLoadingCompany(true);
    try {
      // super_admin uses the full details endpoint; others use /my
      const company: any = isSuperAdmin
        ? (await companyApi.getCompanyDetails(companyId)).data.company
        : (await companyApi.getMyCompany()).data;
      setCompanyForm({
        name: company.name || '',
        contactEmail: company.contactEmail || '',
        address: company.address || '',
        phone: company.phone || '',
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('settings.company.loadFailed'));
    } finally {
      setIsLoadingCompany(false);
    }
  }, [isSuperAdmin, t]);

  // Load companies list for super_admin; load current company for company_admin
  useEffect(() => {
    if (activeTab !== 'company') return;

    const init = async () => {
      if (isSuperAdmin) {
        try {
          const res = await companyApi.listCompanies();
          const list = res.data;
          setCompanies(list);
          if (list.length > 0) {
            const firstId = list[0]._id;
            setSelectedCompanyId(firstId);
            await loadCompanyData(firstId);
          }
        } catch (error: any) {
          toast.error(error.response?.data?.message || t('settings.company.loadFailed'));
        }
      } else {
        // company_admin: load via /api/companies/my (no company_id needed)
        await loadCompanyData('me');
      }
    };

    init();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCompanySelect = async (companyId: string) => {
    setSelectedCompanyId(companyId);
    await loadCompanyData(companyId);
  };

  const handleCompanySave = async () => {
    if (!selectedCompanyId) return;

    setIsSavingCompany(true);
    try {
      await companyApi.updateCompany(selectedCompanyId, {
        name: companyForm.name,
        ...(companyForm.contactEmail ? { contactEmail: companyForm.contactEmail } : {}),
        ...(companyForm.address ? { address: companyForm.address } : {}),
        ...(companyForm.phone ? { phone: companyForm.phone } : {}),
      } as any);
      toast.success(t('settings.company.savedSuccess'));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('settings.company.saveFailed'));
    } finally {
      setIsSavingCompany(false);
    }
  };

  // ── Notifications tab state ────────────────────────────────────────────────

  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefs>(() => {
    try {
      const stored = localStorage.getItem(NOTIF_STORAGE_KEY);
      return stored ? { ...defaultNotifPrefs, ...JSON.parse(stored) } : defaultNotifPrefs;
    } catch {
      return defaultNotifPrefs;
    }
  });
  const [isSavingNotif, setIsSavingNotif] = useState(false);

  const handleNotifSave = () => {
    setIsSavingNotif(true);
    // Small artificial delay for UX consistency
    setTimeout(() => {
      localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify(notifPrefs));
      toast.success(t('settings.notifications.savedSuccess'));
      setIsSavingNotif(false);
    }, 300);
  };

  // ── Tab labels ─────────────────────────────────────────────────────────────

  const tabLabel: Record<Tab, string> = {
    profile: t('settings.tabs.profile'),
    company: t('settings.tabs.company'),
    notifications: t('settings.tabs.notifications'),
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('settings.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* ── Left sidebar ─────────────────────────────────────────────── */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
              <nav className="space-y-1">
                {availableTabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors ${
                      activeTab === tab
                        ? 'bg-primary-50 text-primary-600 font-medium'
                        : 'text-gray-700 hover:bg-gray-50 font-normal'
                    }`}
                  >
                    {tabLabel[tab]}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* ── Main content area ─────────────────────────────────────────── */}
          <div className="lg:col-span-3">

            {/* ── Tab: Mon profil ──────────────────────────────────────────── */}
            {activeTab === 'profile' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-6">
                  {t('settings.profile.title')}
                </h2>

                <div className="space-y-5 max-w-lg">
                  {/* Identity */}
                  <Input
                    label={t('settings.profile.fullName')}
                    value={profileForm.name}
                    onChange={(e) =>
                      setProfileForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    error={profileErrors.name}
                    placeholder="Jean Dupont"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('settings.profile.email')}
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="block w-full px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-500 bg-gray-50 cursor-not-allowed"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      {t('settings.profile.emailReadOnly')}
                    </p>
                  </div>

                  <Input
                    label={t('settings.profile.phone')}
                    value={profileForm.phone}
                    onChange={(e) =>
                      setProfileForm((prev) => ({ ...prev, phone: e.target.value }))
                    }
                    error={profileErrors.phone}
                    placeholder="+33612345678"
                  />

                  {/* Divider */}
                  <div className="border-t border-gray-100 pt-5">
                    <p className="text-sm font-medium text-gray-700 mb-4">
                      {t('settings.profile.changePassword')}
                    </p>

                    <div className="space-y-4">
                      <Input
                        label={t('settings.profile.newPassword')}
                        type="password"
                        value={profileForm.newPassword}
                        onChange={(e) =>
                          setProfileForm((prev) => ({ ...prev, newPassword: e.target.value }))
                        }
                        error={profileErrors.newPassword}
                        placeholder={t('settings.profile.newPasswordPlaceholder')}
                        helperText={t('settings.profile.passwordHelper')}
                      />

                      <Input
                        label={t('settings.profile.confirmPassword')}
                        type="password"
                        value={profileForm.confirmPassword}
                        onChange={(e) =>
                          setProfileForm((prev) => ({
                            ...prev,
                            confirmPassword: e.target.value,
                          }))
                        }
                        error={profileErrors.confirmPassword}
                        placeholder={t('settings.profile.confirmPasswordPlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="pt-2">
                    <Button
                      onClick={handleProfileSave}
                      disabled={isSavingProfile}
                      className="btn-primary"
                    >
                      {isSavingProfile
                        ? t('settings.profile.saving')
                        : t('settings.profile.save')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Tab: Entreprise ──────────────────────────────────────────── */}
            {activeTab === 'company' && isCompanyAdmin && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-6">
                  {t('settings.company.title')}
                </h2>

                {/* Super admin company selector */}
                {isSuperAdmin && companies.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('settings.company.selectCompany')}
                    </label>
                    <select
                      value={selectedCompanyId}
                      onChange={(e) => handleCompanySelect(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {companies.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {isLoadingCompany ? (
                  <FieldSkeleton />
                ) : (
                  <div className="space-y-5 max-w-lg">
                    <Input
                      label={t('settings.company.name')}
                      value={companyForm.name}
                      onChange={(e) =>
                        setCompanyForm((prev) => ({ ...prev, name: e.target.value }))
                      }
                      placeholder="Acme Corporation"
                    />

                    <Input
                      label={t('settings.company.contactEmail')}
                      type="email"
                      value={companyForm.contactEmail}
                      onChange={(e) =>
                        setCompanyForm((prev) => ({ ...prev, contactEmail: e.target.value }))
                      }
                      placeholder="contact@acme.com"
                    />

                    <Input
                      label={t('settings.company.address')}
                      value={companyForm.address}
                      onChange={(e) =>
                        setCompanyForm((prev) => ({ ...prev, address: e.target.value }))
                      }
                      placeholder="123 Rue de la Paix, Paris, France"
                    />

                    <Input
                      label={t('settings.company.phone')}
                      value={companyForm.phone}
                      onChange={(e) =>
                        setCompanyForm((prev) => ({ ...prev, phone: e.target.value }))
                      }
                      placeholder="+33140123456"
                    />

                    <div className="pt-2">
                      <Button
                        onClick={handleCompanySave}
                        disabled={isSavingCompany || !selectedCompanyId}
                        className="btn-primary"
                      >
                        {isSavingCompany
                          ? t('settings.company.saving')
                          : t('settings.company.save')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Tab: Notifications ───────────────────────────────────────── */}
            {activeTab === 'notifications' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-2">
                  {t('settings.notifications.title')}
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                  {t('settings.notifications.subtitle')}
                </p>

                <div className="divide-y divide-gray-100">
                  <Toggle
                    checked={notifPrefs.newTrips}
                    onChange={(v) => setNotifPrefs((prev) => ({ ...prev, newTrips: v }))}
                    label={t('settings.notifications.newTrips')}
                    description={t('settings.notifications.newTripsDesc')}
                  />
                  <Toggle
                    checked={notifPrefs.qualificationReminders}
                    onChange={(v) =>
                      setNotifPrefs((prev) => ({ ...prev, qualificationReminders: v }))
                    }
                    label={t('settings.notifications.qualificationReminders')}
                    description={t('settings.notifications.qualificationRemindersDesc')}
                  />
                  <Toggle
                    checked={notifPrefs.emailNotifications}
                    onChange={(v) =>
                      setNotifPrefs((prev) => ({ ...prev, emailNotifications: v }))
                    }
                    label={t('settings.notifications.emailNotifications')}
                    description={t('settings.notifications.emailNotificationsDesc')}
                  />
                </div>

                <div className="pt-6">
                  <Button
                    onClick={handleNotifSave}
                    disabled={isSavingNotif}
                    className="btn-primary"
                  >
                    {isSavingNotif
                      ? t('settings.notifications.saving')
                      : t('settings.notifications.save')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
